/** Стартовый экран.
 *
 * Порядок блоков отвечает на вопрос «что делать прямо сейчас», а не
 * перечисляет разделы: сначала незаконченная партия (если она есть), потом
 * задача дня, потом выбор уровня. Карточка на акценте ровно одна — то
 * действие, которое игрок с наибольшей вероятностью и хотел.
 */

import { useState } from 'react'

import type { DailyChallenge, Difficulty, GameMode, GameState, PlayerStats, User } from '../api/types'
import { Dialog } from '../components/Sheet'
import { BalanceChip, DIFFICULTIES, LevelBars, ProgressBar } from '../components/ui'
import { formatDay, formatNumber, formatTime } from '../game/format'
import { pluralOf, t } from '../i18n'

const CELL_COUNT = 81

export interface HomeScreenProps {
  user: User | null
  balance: number
  currentGame: GameState | null
  daily: DailyChallenge | null
  stats: PlayerStats | null
  busy: boolean
  onContinue: () => void
  onStart: (difficulty: Difficulty, mode: GameMode) => void
  onStartDaily: () => void
  onOpenRating: () => void
  onOpenShop: () => void
  onOpenSettings: () => void
}

export function HomeScreen({
  user,
  balance,
  currentGame,
  daily,
  stats,
  busy,
  onContinue,
  onStart,
  onStartDaily,
  onOpenRating,
  onOpenShop,
  onOpenSettings,
}: HomeScreenProps) {
  // Выбор режима — отдельный шаг, а не переключатель где-то в настройках:
  // хардкор должен быть осознанным решением, иначе он читается как
  // наказание за первую же ошибку.
  const [pendingLevel, setPendingLevel] = useState<Difficulty | null>(null)
  const streak = stats?.current_streak ?? 0

  return (
    <div className="screen">
      <header className="topbar">
        <span className="topbar__spacer" />
        <span className="topbar__title">{t('app.title')}</span>
        <button
          type="button"
          className="topbar__action"
          onClick={onOpenSettings}
          aria-label={t('settings.title')}
        >
          ⚙
        </button>
      </header>

      <div className="screen__body">
        <div className="profile">
          <div className="profile__person">
            {user?.photo_url ? (
              <img className="avatar" src={user.photo_url} alt="" />
            ) : (
              <span className="avatar">{(user?.display_name ?? '?').slice(0, 1)}</span>
            )}
            <span>
              <span className="profile__name">{user?.display_name}</span>
              <span className="hint-text" style={{ display: 'block' }}>
                {streak > 0
                  ? t('home.streak', { days: streak, word: pluralOf('plural.days', streak) })
                  : t('home.noStreak')}
              </span>
            </span>
          </div>
          <BalanceChip balance={balance} />
        </div>

        {currentGame && <ContinueCard game={currentGame} onContinue={onContinue} />}

        {daily && (
          <DailyCard
            daily={daily}
            onPlay={onStartDaily}
            busy={busy}
            featured={!currentGame && !daily.solved}
          />
        )}

        <section className="home-section" aria-labelledby="new-game-heading">
          <div className="home-section__heading">
            <span id="new-game-heading" className="section-label">
              {t('home.newGame')}
            </span>
            <span className="hint-text">{t('home.newGameHint')}</span>
          </div>
          <div className="level-grid">
            {DIFFICULTIES.map((difficulty) => (
              <LevelTile
                key={difficulty}
                difficulty={difficulty}
                best={stats?.best_times?.[difficulty] ?? null}
                disabled={busy}
                onSelect={() => setPendingLevel(difficulty)}
              />
            ))}
          </div>
        </section>

        <div className="home-links push-down" aria-label={t('home.more')}>
          <button type="button" className="home-link" onClick={onOpenRating}>
            <span className="home-link__icon" aria-hidden="true">⌁</span>
            <span>
              <span className="home-link__title">{t('home.rating')}</span>
              <span className="hint-text">{t('home.ratingHint')}</span>
            </span>
          </button>
          <button type="button" className="home-link" onClick={onOpenShop}>
            <span className="home-link__icon" aria-hidden="true">◆</span>
            <span>
              <span className="home-link__title">{t('home.shop')}</span>
              <span className="hint-text">{t('home.shopHint')}</span>
            </span>
          </button>
        </div>
      </div>

      {pendingLevel && (
        <ModeDialog
          difficulty={pendingLevel}
          onPick={(mode) => {
            setPendingLevel(null)
            onStart(pendingLevel, mode)
          }}
          onDismiss={() => setPendingLevel(null)}
        />
      )}
    </div>
  )
}

function ContinueCard({ game, onContinue }: { game: GameState; onContinue: () => void }) {
  return (
    <button type="button" className="card card--accent" onClick={onContinue}>
      <div className="row">
        <span>
          <span style={{ fontSize: 'var(--size-timer)', fontWeight: 600, display: 'block' }}>
            {t('home.continue')}
          </span>
          <span style={{ fontSize: 'var(--size-hint)', opacity: 0.85 }}>
            {t('home.continueMeta', {
              level: t(`level.${game.difficulty}`),
              time: formatTime(game.elapsed_ms),
              filled: game.filled_count,
            })}
          </span>
        </span>
        <span style={{ fontSize: 22, opacity: 0.8 }}>›</span>
      </div>
      <div style={{ marginTop: 'var(--gap-4)' }}>
        <ProgressBar value={game.filled_count} total={CELL_COUNT} variant="accent" />
      </div>
    </button>
  )
}

function DailyCard({
  daily,
  onPlay,
  busy,
  featured = false,
}: {
  daily: DailyChallenge
  onPlay: () => void
  busy: boolean
  featured?: boolean
}) {
  const level = t(`level.${daily.difficulty}`)

  // Решённая задача дня не предлагает сыграть ещё раз: второй результат
  // за день всё равно не засчитается, и кнопка обманывала бы.
  if (daily.solved) {
    return (
      <div className="card card--success">
        <div className="row">
          <span>
            <span className="hint-text" style={{ display: 'block', color: 'inherit' }}>
              {t('home.daily', { date: formatDay(daily.date) })}
            </span>
            <span style={{ fontSize: 'var(--size-row)', fontWeight: 600, display: 'block' }}>
              {t('home.dailySolved', { time: formatTime(daily.elapsed_ms ?? 0) })}
            </span>
            <span className="hint-text" style={{ color: 'inherit', opacity: 0.85 }}>
              {daily.better_than_percent === null
                ? t('home.dailyDone')
                : t('home.dailyBetter', { percent: daily.better_than_percent })}
            </span>
          </span>
          <span className="badge badge--done">✓</span>
        </div>
      </div>
    )
  }

  return (
    <div className={`card daily-card ${featured ? 'card--accent' : ''}`.trim()}>
      <div className="row">
        <span>
          <span className="hint-text" style={{ display: 'block' }}>
            {t('home.daily', { date: formatDay(daily.date) })}
          </span>
          <span style={{ fontSize: 'var(--size-row)', fontWeight: 600, display: 'block' }}>
            {t('home.dailyTitle')}
          </span>
          <span className="hint-text">
            {daily.solvers > 0
              ? t('home.dailySolvers', { level, count: formatNumber(daily.solvers) })
              : t('home.dailyMeta', { level })}
          </span>
        </span>
        <button
          type="button"
          className={`button button--compact ${featured ? 'button--inverted' : 'button--primary'}`}
          onClick={onPlay}
          disabled={busy}
        >
          {featured ? t('home.dailyPlay') : t('home.play')}
        </button>
      </div>
    </div>
  )
}

function LevelTile({
  difficulty,
  best,
  disabled,
  onSelect,
}: {
  difficulty: Difficulty
  best: number | null
  disabled: boolean
  onSelect: () => void
}) {
  return (
    <button type="button" className="level-tile" onClick={onSelect} disabled={disabled}>
      <LevelBars difficulty={difficulty} />
      <span className="level-tile__body">
        <span className="level-tile__title">
          {t(`level.${difficulty}`)}
        </span>
        <span className="hint-text">{t(`level.${difficulty}.hint`)}</span>
      </span>
      <span className={`level-tile__best ${best ? '' : 'muted-text'}`.trim()}>
        {best ? (
          <>
            <span>{t('home.best')}</span>
            <span className="num">{formatTime(best)}</span>
          </>
        ) : (
          t('home.noRecord')
        )}
      </span>
    </button>
  )
}

/**
 * Выбор режима перед стартом.
 *
 * Множитель очков рядом с хардкором делает лимит ошибок сделкой, а не
 * ловушкой: в транспорте человек выберет «спокойно» и не бросит игру
 * после третьей ошибки.
 */
function ModeDialog({
  difficulty,
  onPick,
  onDismiss,
}: {
  difficulty: Difficulty
  onPick: (mode: GameMode) => void
  onDismiss: () => void
}) {
  const [mode, setMode] = useState<GameMode>('normal')

  return (
    <Dialog title={t('mode.title', { level: t(`level.${difficulty}`) })} onDismiss={onDismiss}>
      <ModeOption
        title={t('mode.calm')}
        hint={t('mode.calmHint')}
        note="×1,0"
        active={mode === 'normal'}
        onSelect={() => setMode('normal')}
      />
      <ModeOption
        title={t('mode.hardcore')}
        hint={t('mode.hardcoreHint')}
        note={t('mode.bonus', { value: '×1,5' })}
        highlight
        active={mode === 'hardcore'}
        onSelect={() => setMode('hardcore')}
      />
      <button type="button" className="button button--primary" onClick={() => onPick(mode)}>
        {t('mode.start')}
      </button>
    </Dialog>
  )
}

function ModeOption({
  title,
  hint,
  note,
  active,
  highlight = false,
  onSelect,
}: {
  title: string
  hint: string
  note: string
  active: boolean
  highlight?: boolean
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      className="list__row"
      onClick={onSelect}
      style={{
        borderRadius: 'var(--radius-button)',
        border: 'none',
        boxShadow: active ? 'inset 0 0 0 1.5px var(--accent)' : 'none',
        background: 'var(--bg-secondary)',
        padding: '12px',
      }}
      aria-pressed={active}
    >
      <span className="list__grow">
        <span className="profile__name" style={{ display: 'block' }}>
          {title}
        </span>
        <span className="hint-text">{hint}</span>
      </span>
      <span
        className="hint-text"
        style={{ fontWeight: 600, color: highlight ? 'var(--success)' : undefined }}
      >
        {note}
      </span>
    </button>
  )
}
